import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingDots from '../../components/LoadingDots/LoadingDots';
import { acceptQuoteFromExternal } from '../../api/apiServices';
import { getBaseProductRoute } from '../../utils/productNavigation';
import { FLOW_SLUG_CONFIG } from '../../utils/flowSlugConfig';

// Landing point for externally shared quote links, e.g.
//   /car-insurance-quote/?personId=12630786&quoteId=69946906
// Accepts the quote via the external endpoint and forwards the user straight to
// the thank you page, populated with the returned quote details.
function ExternalQuoteAccept({ quoteId, personId }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const hasRun = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode's double-invocation in development so the
    // quote isn't accepted twice.
    if (hasRun.current) return;
    hasRun.current = true;

    const flowSlug = getBaseProductRoute(pathname).replace(/^\//, '');
    const product = FLOW_SLUG_CONFIG[flowSlug]?.product || flowSlug;

    const accept = async () => {
      try {
        const result = await acceptQuoteFromExternal({
          QuotePK: quoteId,
          PersonPK: personId,
          CallBackTime: '',
        });

        // Endpoints in this API sometimes return the payload wrapped in an array.
        const quote = Array.isArray(result) ? result[0] : result;

        if (!quote) {
          throw new Error('No quote details returned');
        }

        navigate('/quoteSelection', {
          replace: true,
          state: {
            ...quote,
            QuotePK: quote.QuotePK || quoteId,
            _product: product,
            _externalAccept: true,
          },
        });
      } catch (err) {
        // Fall back to the normal product landing page (without the query
        // string) so the user can quote from scratch.
        navigate(getBaseProductRoute(pathname) + '/', { replace: true });
      }
    };

    accept();
  }, [quoteId, personId, pathname, navigate]);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F3F6F6',
        zIndex: 2,
      }}
    >
      <LoadingDots variant="otp" />
    </Box>
  );
}

export default ExternalQuoteAccept;
